import { useEffect, useMemo, useState } from 'react';
import {
    sortScansNewestFirst,
    useDmScanDetail,
    useDmScans,
    type DmScanDetail,
    type DmScanSummary
} from '@odo/shared-ui';
import { dmScansQueryConfig } from '../../../../../services/dmScans';
import { markupPhotos, type MarkupPhoto } from './scanPhotoOptions';

export type MarkupPhotosState =
    | { status: 'loading' }
    | { status: 'error'; message: string; retry: () => void }
    | { status: 'no-scans' }
    | { status: 'no-photos' }
    | { status: 'ready'; photos: MarkupPhoto[] };

export interface MarkupScanPhotos {
    scans: DmScanSummary[];
    scan: DmScanSummary | null;
    photos: MarkupPhotosState;
}

interface UseMarkupScanPhotosArgs {
    patientId: string;
    dmId: number | null;
    selectedScanId: number | null;
}

interface PhotosStateInput {
    listLoading: boolean;
    listFailed: boolean;
    retryList: () => void;
    scanId: number | null;
    detailFailed: boolean;
    retryDetail: () => void;
    fresh: boolean;
    detail: DmScanDetail | null | undefined;
}

const LIST_ERROR = 'Could not load this patient’s scans.';
const DETAIL_ERROR = 'Could not load the photos for this scan.';

function photosState(input: PhotosStateInput): MarkupPhotosState {
    if (input.listLoading) return { status: 'loading' };
    if (input.listFailed) return { status: 'error', message: LIST_ERROR, retry: input.retryList };
    if (input.scanId == null) return { status: 'no-scans' };
    if (input.detailFailed) {
        return { status: 'error', message: DETAIL_ERROR, retry: input.retryDetail };
    }
    if (!input.fresh) return { status: 'loading' };
    const photos = markupPhotos(input.detail?.photos);
    return photos.length === 0 ? { status: 'no-photos' } : { status: 'ready', photos };
}

// Signed photo URLs last an hour, so photos cached before the picker opened are refetched first.
export function useMarkupScanPhotos({
    patientId,
    dmId,
    selectedScanId
}: UseMarkupScanPhotosArgs): MarkupScanPhotos {
    const [openedAt] = useState(() => Date.now());
    const list = useDmScans(dmScansQueryConfig, patientId, dmId);
    const scans = useMemo(() => sortScansNewestFirst(list.data?.scans ?? []), [list.data]);
    const scan = scans.find((item) => item.scanId === selectedScanId) ?? scans[0] ?? null;
    const scanId = scan?.scanId ?? null;

    const detail = useDmScanDetail(dmScansQueryConfig, patientId, scanId);
    const { refetch, isFetching, isError } = detail;
    const fresh = detail.data !== undefined && detail.dataUpdatedAt >= openedAt;

    useEffect(() => {
        if (scanId != null && !fresh && !isFetching && !isError) void refetch();
    }, [scanId, fresh, isFetching, isError, refetch]);

    const photos = photosState({
        listLoading: list.isLoading,
        listFailed: list.isError && !list.data,
        retryList: () => void list.refetch(),
        scanId,
        detailFailed: isError && !isFetching,
        retryDetail: () => void refetch(),
        fresh,
        detail: detail.data
    });

    return { scans, scan, photos };
}
